import type { QueryClient } from '@tanstack/react-query';
import {
  Users,
  Box,
  Boxes,
  DollarSign,
  Ticket,
  Gift,
  Receipt,
  Package,
  Camera,
  Layers,
  Database,
  ArrowRightLeft,
  Languages,
  Cog,
  Upload,
  Settings,
  Shield,
  KeyRound,
} from 'lucide-react';
import { Link, redirect, type RouteObject } from 'react-router-dom';

import { getGamesQueryOptions } from '@/api/getGames';
import { getMyPermissionsQueryOptions } from '@/api/getMyPermissions';
import { getSandboxesQueryOptions } from '@/api/getSandboxes';
import { getTranslationLanguagesQuery } from '@/api/translation/getTranslationLanguages';
import { firstAllowed, requirePermissions } from '@/app/requirePermissions';
import { SandboxBreadcrumbDropdown } from '@/features/sandbox/components/sandboxBreadcrumbDropdown';
import { ensureAppQueryData } from '@/hooks/useAppQuery';
import { updateApiDefaults } from '@/hooks/useGameSandboxSwitch';
import { SANDBOX_LOCAL_STORAGE, storeGameSandbox } from '@/lib/utils';
import type { Game } from '@/models/game';
import { PagePermissions } from '@/models/pagePermissions';
import { Permissions } from '@/models/permission';
import type { PlayerModel } from '@/models/players';

import { accessRoute } from './access/route';
import Dashboard from './dashboard';
import { GameLayout } from './GameLayout';
import { giftRoute } from './gift/route';
import { itemRoute } from './items/route';
import { seasonPassRoute } from './seasonPass/route';
import { settingsRoute } from './settings/route';
import { storeRoute } from './store/route';
import { transactionsRoute } from './transactions/route';
import { translationsRoute } from './translations/route';

const MainDashboard = (
  <Dashboard
    menu
    items={[
      {
        link: 'players',
        label: 'Players',
        icon: <Users size={20} />,
        items: [],
        permissions: PagePermissions.players,
      },
      {
        link: 'items',
        label: 'Catalog',
        icon: <Database size={20} />,
        permissions: PagePermissions.catalogItems,
        items: [
          {
            link: 'item',
            label: 'Items',
            icon: <Box size={16} />,
            permissions: PagePermissions.catalogItems,
          },
          {
            link: 'bundle',
            label: 'Bundles',
            icon: <Boxes size={16} />,
            permissions: PagePermissions.catalogBundles,
          },
          {
            link: 'currency',
            label: 'Currency',
            icon: <DollarSign size={16} />,
            permissions: PagePermissions.catalogCurrency,
          },
        ],
      },
      {
        link: 'season-passes',
        label: 'Season Pass',
        icon: <Ticket size={20} />,
        items: [],
        permissions: PagePermissions.seasonPasses,
      },
      {
        link: 'gifts',
        label: 'Gifts',
        icon: <Gift size={20} />,
        items: [],
        permissions: PagePermissions.gifts,
      },
      {
        link: 'translations',
        label: 'Translations',
        icon: <Languages size={20} />,
        items: [],
        permissions: PagePermissions.translations,
      },
      {
        link: 'transactions',
        label: 'Transactions',
        icon: <ArrowRightLeft size={20} />,
        items: [
          {
            link: 'store',
            label: 'Store',
            icon: <Receipt size={20} />,
            permissions: PagePermissions.transactionsStore,
          },
          {
            link: 'inventory',
            label: 'Inventory',
            icon: <Package size={20} />,
            permissions: PagePermissions.transactionsInventory,
          },
        ],
      },
      {
        link: 'store/snapshot',
        label: 'Snapshot',
        icon: <Camera size={16} />,
        permissions: PagePermissions.snapshots,
      },
      {
        // No permissions of its own: the section shows whenever one of its pages does,
        // which keeps Import reachable for an importer holding neither edit code.
        link: 'settings',
        label: 'Settings',
        icon: <Cog size={20} />,
        items: [
          {
            // Its two sections: Naming writes the Sandbox, Supported Languages writes
            // the language list. Holding either is enough to open the page.
            link: 'general',
            label: 'General',
            icon: <Settings size={16} />,
            permissions: PagePermissions.settingsGeneral,
          },
          {
            link: 'import',
            label: 'Import',
            icon: <Upload size={16} />,
            permissions: PagePermissions.settingsImport,
          },
        ],
      },
    ]}
  />
);

// Game-level dashboard: sandboxes and access administration
const GameDashboard = (
  <Dashboard
    menu
    items={[
      {
        link: 'sandboxes',
        label: 'Sandboxes',
        icon: <Layers size={20} />,
        items: [],
      },
      {
        // The section and the Role catalogue are readable by anyone signed in (RBAC
        // Design Document); only the user directory is gated. The link asks for the
        // Game-scoped permission that opens the area — the directory itself needs
        // users:read at global scope, which the page states rather than 403s on.
        link: 'access',
        label: 'Access',
        icon: <Shield size={20} />,
        items: [
          { link: 'roles', label: 'Roles', icon: <KeyRound size={16} /> },
          {
            link: 'members',
            label: 'Users',
            icon: <Users size={16} />,
            permissions: [Permissions.admin.managePermissions],
          },
        ],
      },
    ]}
  />
);

export const gameRoute = (queryClient: QueryClient): RouteObject => ({
  path: 'game/:gameCode',
  id: 'game',
  handle: {
    crumb: (game: Game) => (
      <Link to={`/game/${game.game_code}/sandboxes`}>{game.name}</Link>
    ),
  },
  loader: ({ params }) => {
    // Warm the viewer's own Grants: the sidebar gates every entry on them and fails
    // closed, so without this the menu paints empty on a cold cache. Non-blocking —
    // navigation shouldn't wait on it. The catch keeps a failed read from
    // becoming an unhandled rejection — the gates handle the failure themselves.
    void queryClient
      .ensureQueryData(getMyPermissionsQueryOptions())
      .catch(() => undefined);

    return queryClient
      .ensureQueryData(getGamesQueryOptions())
      .then((data) => data.games.find((g) => g.game_code === params.gameCode));
  },
  children: [
    {
      // Index route: redirect to saved sandbox if exists and valid, otherwise to sandboxes page
      index: true,
      loader: async ({ params }) => {
        const { gameCode } = params;
        if (gameCode) {
          const savedSandboxes = localStorage.getItem(SANDBOX_LOCAL_STORAGE);
          if (savedSandboxes) {
            const parsed = JSON.parse(savedSandboxes) as {
              game: string;
              sandbox: string;
            }[];
            const found = parsed.find((s) => s.game === gameCode);
            if (found) {
              // Validate that the saved sandbox still exists
              const { sandboxes } = await queryClient.ensureQueryData(
                getSandboxesQueryOptions({ game_code: gameCode }),
              );
              const sandboxExists = sandboxes.some(
                (s) => s.code === found.sandbox,
              );
              if (sandboxExists) {
                return redirect(found.sandbox);
              }
            }
          }
        }
        return redirect('sandboxes');
      },
    },
    // Game-level routes (no sandbox required)
    {
      element: GameDashboard,
      children: [
        {
          path: 'sandboxes',
          lazy: () => import('./sandboxes'),
        },
        { ...accessRoute },
      ],
    },
    // Sandbox-level routes
    {
      path: ':sandboxCode',
      id: 'sandbox', // Used by SandboxBreadcrumbDropdown to get the sandbox match data
      handle: {
        crumb: ({ sandboxCode }: { sandboxCode: string }) => (
          <SandboxBreadcrumbDropdown sandboxCode={sandboxCode} />
        ),
      },
      loader: ({ params }) => {
        const { gameCode, sandboxCode } = params;
        if (!gameCode || !sandboxCode) {
          throw new Error('Game code and sandbox code are required');
        }
        // Set Axios defaults using shared utility - overwrites previous values on each navigation
        updateApiDefaults(gameCode, sandboxCode);

        // Persist to localStorage for next visit
        storeGameSandbox(gameCode, sandboxCode);

        // Warm the sandbox-scoped language list so it's available everywhere
        // (e.g. the item modal) without each consumer refetching. Non-blocking:
        // navigation shouldn't wait on it.
        void ensureAppQueryData(
          queryClient,
          { gameCode, sandboxCode },
          getTranslationLanguagesQuery(),
        );

        return { sandboxCode };
      },
      element: <GameLayout />,
      children: [
        {
          // Not always 'players': that needs players:read, which most Roles lack. Lands
          // on the first sidebar entry this viewer can open, in sidebar order.
          index: true,
          loader: firstAllowed(
            [
              { to: 'players', oneOf: PagePermissions.players },
              { to: 'items', oneOf: PagePermissions.catalogItems },
              { to: 'items/bundle', oneOf: PagePermissions.catalogBundles },
              { to: 'season-passes', oneOf: PagePermissions.seasonPasses },
              { to: 'gifts', oneOf: PagePermissions.gifts },
              { to: 'translations', oneOf: PagePermissions.translations },
              { to: 'transactions', oneOf: PagePermissions.transactionsStore },
              {
                to: 'transactions/inventory',
                oneOf: PagePermissions.transactionsInventory,
              },
              { to: 'store/snapshot', oneOf: PagePermissions.snapshots },
              { to: 'settings', oneOf: PagePermissions.settingsGeneral },
              { to: 'settings/import', oneOf: PagePermissions.settingsImport },
            ],
            // The sandbox landing is chosen for the user (the Game index redirects
            // here), so an empty Sandbox sends them back to the ungated Sandbox list
            // rather than to a 403 they did not ask for.
            ({ gameCode }) => `/game/${gameCode}/sandboxes`,
          ),
        },
        {
          element: MainDashboard,
          children: [
            {
              path: 'players',
              loader: requirePermissions({ oneOf: PagePermissions.players }),
              children: [
                {
                  index: true,
                  lazy: () => import('./players'),
                },
                {
                  path: ':playerId',
                  id: 'player',
                  handle: {
                    crumb: ({ player }: { player: PlayerModel }) =>
                      player.platform_user_id,
                  },
                  lazy: async () => {
                    const { loader, Player } = await import('./player');
                    return {
                      loader: loader(queryClient),
                      Component: Player,
                    };
                  },
                },
              ],
            },
            { ...itemRoute },
            { ...seasonPassRoute },
            { ...giftRoute },
            { ...translationsRoute(queryClient) },
            { ...transactionsRoute },
            { ...storeRoute(queryClient) },
            { ...settingsRoute },
          ],
        },
      ],
    },
  ],
});
